/**
 * Query keys: Move-In, Quarterly Check-in, Unit Condition Check, Recertification, Renewal, Survey Outreach, Property Manual, Business Review dashboards.
 *
 * Keys are arrays that start with a stable domain prefix so `invalidateQueries`
 * can target a whole domain by prefix. Put every input the response depends on
 * (account, user, portfolio ids, filters, date range) into the key.
 */

/** Portfolio ids are a set on the server, so the key holds a sorted numeric copy. */
const sortedIds = (ids) =>
  (Array.isArray(ids) ? ids : [])
    .map(Number)
    .filter((id) => Number.isFinite(id))
    .sort((left, right) => left - right)

/** A custom range only counts when both ends are set; otherwise the preset `days` applies. */
const rangeOf = (dateRange) =>
  dateRange?.startDate && dateRange?.endDate
    ? { startDate: dateRange.startDate, endDate: dateRange.endDate }
    : null

/**
 * Dashboard product segments. Each is the second key element, so
 * `insightsSurveysKeys.product(PRODUCTS.propertyManual)` invalidates one dashboard.
 */
export const INSIGHTS_SURVEY_PRODUCTS = Object.freeze({
  moveInSurveys: 'moveInSurveys',
  quarterlyCheckins: 'quarterlyCheckins',
  unitConditionChecks: 'unitConditionChecks',
  recertification: 'recertification',
  renewals: 'renewals',
  surveyOutreachSettings: 'surveyOutreachSettings',
  propertyManual: 'propertyManual',
  businessReview: 'businessReview',
})

export const insightsSurveysKeys = {
  all: ['insightsSurveys'],

  /** Every cached read for one dashboard product. */
  product: (product) => ['insightsSurveys', product],

  /**
   * One dashboard section read. `params` holds every other request input
   * (days, paging, search, sort, lookahead); pass only what the endpoint uses so
   * unrelated filter changes do not refetch it.
   */
  section: (product, section, { accountId, portfolioIds, dateRange, ...params } = {}) => [
    'insightsSurveys',
    product,
    section,
    {
      accountId: accountId ?? null,
      portfolioIds: sortedIds(portfolioIds),
      dateRange: rangeOf(dateRange),
      ...params,
    },
  ],

  /** One recertification thread's escalation emails. */
  recertificationEscalationEmails: (threadId) => [
    'insightsSurveys',
    INSIGHTS_SURVEY_PRODUCTS.recertification,
    'escalationEmails',
    String(threadId),
  ],

  /** Renewal dashboard job start for one account, portfolio set, and lease window. */
  renewalDashboardJob: ({ accountId, portfolioIds, startDate, endDate }) => [
    'insightsSurveys',
    INSIGHTS_SURVEY_PRODUCTS.renewals,
    'dashboardJob',
    { accountId: accountId ?? null, portfolioIds: sortedIds(portfolioIds), startDate, endDate },
  ],

  /** Status of one renewal dashboard job. */
  renewalDashboardJobStatus: (jobId) => [
    'insightsSurveys',
    INSIGHTS_SURVEY_PRODUCTS.renewals,
    'dashboardJobStatus',
    jobId ?? null,
  ],

  /**
   * Latest Business Review or ROI report for one account and portfolio set.
   * `source: 'cached'` is the stored report read shown before the first run;
   * `source: 'run'` holds the report of the most recent completed run.
   */
  businessReviewLatestReport: ({ accountId, reportType, portfolioIds, source }) => [
    'insightsSurveys',
    INSIGHTS_SURVEY_PRODUCTS.businessReview,
    'latestReport',
    { accountId: accountId ?? null, reportType, portfolioIds: sortedIds(portfolioIds), source },
  ],

  /**
   * One requested report run. `runVersion` is the Run/Refresh click count, so an
   * explicit refresh starts a new job while re-renders reuse the same start.
   */
  businessReviewRun: ({ accountId, reportType, portfolioIds, filters, runVersion }) => [
    'insightsSurveys',
    INSIGHTS_SURVEY_PRODUCTS.businessReview,
    'run',
    {
      accountId: accountId ?? null,
      reportType,
      portfolioIds: sortedIds(portfolioIds),
      filters,
      runVersion,
    },
  ],

  /** Status of one Business Review, ROI, or Slides job. */
  businessReviewJobStatus: (jobKind, jobId) => [
    'insightsSurveys',
    INSIGHTS_SURVEY_PRODUCTS.businessReview,
    'jobStatus',
    jobKind,
    jobId ?? null,
  ],
}
